import {
    FormalSankeyConfig,
    FormalSankeyData,
    FormalSankeyLink,
    NodeValues,
    SankeyDiagramGraph,
    SideCounter
} from "components/dashboard/charts/PlotlySankey/SankeyTypes";
import {dateToReadableString} from "utils/time-utils";
import {DatasetDataInfoResponse} from "../../../../api/model/dataset/DatasetDataInfoResponse";

export const validatedConfigIsCorrect = (graph: SankeyDiagramGraph): string => {
    const nodesId = graph.nodes.map(e => e.id)

    let correct:string = "";
    graph.links.forEach((l) => {
        if (!nodesId.includes(l.source)) {
            correct = l.source
        }
        if(!nodesId.includes(l.target)) {
            correct =  l.target
        }
    })

    return correct
}

const findResponseByIdentifier = (data: Array<DatasetDataInfoResponse>, givenIdentifier: string): DatasetDataInfoResponse | undefined => {
    return data.find(item => item.identifier === givenIdentifier);
};

const getPercentage = (value: number, fullVal: number) => {
    return fullVal > 0 ? (value * 100) / fullVal : 0;
};

function extractValueByNodeName(arr: NodeValues[], nodeName: string) {
    for (let i = 0; i < arr.length; i++) {
        if (arr[i].nodeName === nodeName) {
            return arr[i].value;
        }
    }
    return undefined
}

const extractUniqueNames = (
    graph: SankeyDiagramGraph,
    nodeVal: NodeValues[],
    refValue: number,
    configDecimalPlace?: number,
    configPercentagesDecimalPlaces = 1
): string[] => {
    const mapped = graph.nodes.map(n => {
        const node = nodeVal.find(item => item.nodeName === n.id);
        const val = node ? node.value : 0;
        const index = node ? nodeVal.indexOf(node) : -1;

        return `${n.title}<br> ${getPercentage(val, refValue).toFixed(
            configPercentagesDecimalPlaces
        )}% | ${val.toFixed(configDecimalPlace || nodeVal[index].decimalPlace)} ${nodeVal[index].unit}`
    });

    return Array.from(mapped);
};

const extractValue = (val: number, configDecimalPlace: number) => {
    if (val <= 0.000000000001) {
        return 0
    } else {
        return val.toFixed(configDecimalPlace)
    }
}

const makeCustomData = (value: number | undefined, unit: string | undefined, title: string | undefined, date: string | undefined, sourceNodeTitle: string | undefined, nodeValue: number | undefined) => {
    return `${value && value >= 0.000000000001 ? value : 0} ${unit || ""}<br>${sourceNodeTitle && value && nodeValue ? `${(value * 100 / nodeValue).toFixed(2)}% nuo ${sourceNodeTitle}<br>` : ""}${title || ""}<br>${date ? dateToReadableString(new Date(date)) : ""}`
}

export const transformDataToSankeyData = (
    graph: SankeyDiagramGraph,
    data: DatasetDataInfoResponse[],
    configDecimalPlace?: number,
    configPercentagesDecimalPlaces = 1
): FormalSankeyConfig => {
    const nodeIndices: Record<string, number> = {};

    graph.nodes.forEach((node, i) => {
        nodeIndices[node.id] = i;
    });

    const sideCounter = graph.nodes.map(n => ({
        nodeName: n.id,
        type: graph.links.filter(e => e.target === n.id).length === 0 ? "SOURCE" : "TARGET"
    }) as SideCounter);

    let nodeValues: NodeValues[] = sideCounter.map(e => ({
        nodeName: e.nodeName,
        value: 0,
        decimalPlace: 2,
        unit: ""
    }));


    const tempLinks: FormalSankeyLink[] = graph.links.map(({source, target, identifier}) => {
        const res = findResponseByIdentifier(data, identifier);
        const sourceIndex = nodeIndices[source];
        const targetIndex = nodeIndices[target];

        sideCounter.forEach(n => {
            if (n.nodeName === source && n.type === "SOURCE") {
                nodeValues = nodeValues.map(node => {
                    if (n.nodeName === node.nodeName) {
                        return {
                            nodeName: n.nodeName,
                            value: node.value + (res?.data[0]?.value || 0),
                            decimalPlace: res?.decimalPlaces || 2,
                            unit: res?.unit as string || ""
                        };
                    }
                    return node;
                });
            }

            if (n.nodeName === target && n.type === "TARGET") {
                nodeValues = nodeValues.map(node => {
                    if (n.nodeName === node.nodeName) {
                        return {
                            nodeName: n.nodeName,
                            value: node.value + (res?.data[0]?.value || 0),
                            decimalPlace: res?.decimalPlaces || 2,
                            unit: res?.unit as string || ""
                        };
                    }
                    return node;
                });
            }
        });

        return {
            source: sourceIndex,
            target: targetIndex,
            value: res?.data[0]?.value,
            date: res?.data[0]?.key || "",
            title: res?.title || "",
            linkName: `${graph.nodes[sourceIndex].title} -> ${graph.nodes[targetIndex].title}`,
            unit: res?.unit || "",
            showingValue: extractValue(res?.data[0]?.value || 0, configDecimalPlace || res?.decimalPlaces || 2)
        } as FormalSankeyLink;
    });


    let referenceValue = 0;
    nodeValues.forEach(n => {
        graph.nodes.forEach(g => {
            if (g.id === n.nodeName && g.reference) {
                referenceValue += n.value;
            }
        });
    });
    const nodeNames = extractUniqueNames(graph, nodeValues, referenceValue, configDecimalPlace, configPercentagesDecimalPlaces);

    const links: FormalSankeyData = {
        value: tempLinks.map(e => e.value),
        source: tempLinks.map(e => e.source),
        target: tempLinks.map(e => e.target),
        date: tempLinks.map(e => e.date),
        title: tempLinks.map(e => e.title),
        linkName: tempLinks.map(e => e.linkName),
        customdata: tempLinks.map(e => makeCustomData(e.showingValue, e.unit, e.title, e.date, graph.nodes[e.source].title, extractValueByNodeName(nodeValues, graph.nodes[e.source].id)))
    };

    return {nodeNames, links};
};